import { LazyIconWrapper } from "../ui/LazyIconWrapper";
import { createLazyIcon } from "../../lib/lazy-icons";

// Lazy load de toutes les icônes (X et Plus utilisées dans sidebar/éditeur conditionnels)
const X = createLazyIcon("X");
const Check = createLazyIcon("Check");
const ExternalLink = createLazyIcon("ExternalLink");
const Lock = createLazyIcon("Lock");

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EyeOff, PanelLeftClose, PanelLeftOpen, Sparkles } from "lucide-react";
import { useConversation } from "../conversation/ConversationProvider";
import {
  useEditorActions,
  useEditorState,
} from "../editor/EditorStateProvider";
import { useUIState } from "../prototype/UIStateProvider";
import { useMediaQuery } from "@core/hooks/useMediaQuery";
import GeminiChatInterface, {
  type GeminiChatHandle,
} from "../GeminiChatInterface";
import {
  FormSimulationIntegration,
  SimulationButton,
  SimulationModal,
  SimulationReport,
} from "../../exports/products";
import { WorkspaceEditor } from "./WorkspaceEditor";
import { SaveEmailsAsGroupCta } from "../groups/SaveEmailsAsGroupCta";
import { MobileNavigationTabs as MobileNavTabs } from "../layout/MobileNavigationTabs";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { type Poll } from "../../lib/pollStorage";
import {
  getCurrentUserId,
  getOwnerTokenForPoll,
} from "../../lib/guestIdentityService";
import { usePoll } from "../../hooks/usePoll";
import { useWorkspaceNavigation } from "../../hooks/workspace/useWorkspaceNavigation";
import { useWorkspaceData } from "../../hooks/workspace/useWorkspaceData";
import { useWorkspaceDependencies } from "../../hooks/workspace/useWorkspaceDependencies";
import { WorkspaceModals } from "../modals/WorkspaceModals";
import { useToast } from "../../hooks/use-toast";
import { QRCodeDisplay } from "../shared/QRCodeDisplay";
import { UsageAnalyticsTracker } from "../../services/UsageAnalyticsTracker";
import { linkPublishedPollToConversation } from "../../lib/ConversationPollLink";
import { logger } from "../../lib/logger";

// Nouveaux imports pour le refactoring (ResponsiveLayout)
import { ResponsiveLayout } from "../layout/ResponsiveLayout";
import { ChatContainer } from "../layout/ChatContainer";
import { ProductHeader } from "../layout/products/ProductHeader";
import { canOpenPollEditor } from "../../lib/canEditPoll";

/**
 * AI Creation Workspace - Espace de création avec IA
 *
 * Layout avec :
 * - Messages à gauche (chat IA)
 * - Créateur de sondage/formulaire à droite (par défaut)
 * - Input en bas
 */
const AI_PANEL_MODE_KEY = "dd_ai_panel_mode";

interface AICreationWorkspaceProps {
  pollTypeFromUrl?: string;
  hideSidebar?: boolean;
  externalHeaderHeight?: number; // Hauteur du header externe en pixels (ex: 64 pour h-16)
}

export function AICreationWorkspace({
  pollTypeFromUrl: pollTypeFromProp,
  hideSidebar = false,
  externalHeaderHeight,
}: AICreationWorkspaceProps = {}) {
  const { t } = useTranslation(["creator", "common", "errors"]);
  const navigate = useNavigate();
  const location = useLocation();
  const chatRef = useRef<GeminiChatHandle>(null);
  const { toast } = useToast();

  // Lire les paramètres de l'URL pour forcer le remontage du chat
  const searchParams = new URLSearchParams(location.search);
  const resumeId = searchParams.get("resume");
  const conversationId = searchParams.get("conversationId");
  const editPollId = searchParams.get("edit");
  const newChatTimestamp = searchParams.get("new");

  // Priorité : prop > pathname > query param > défaut "date"
  const pollTypeFromPathname = location.pathname.includes("/workspace/form")
    ? "form"
    : location.pathname.includes("/workspace/date")
      ? "date"
      : location.pathname.includes("/workspace/availability")
        ? "availability"
        : null;
  const pollTypeFromQuery = searchParams.get("type") as
    | "date"
    | "form"
    | "availability"
    | null;
  const pollTypeFromUrl =
    pollTypeFromProp || pollTypeFromPathname || pollTypeFromQuery || "date";

  // Convertir "availability" en "date" pour les composants qui ne supportent que "date" | "form"
  // MAIS GeminiChatInterface supporte maintenant "availability"
  const pollTypeForComponents: "date" | "form" | "availability" | "quizz" =
    pollTypeFromUrl === "availability"
      ? "availability"
      : pollTypeFromUrl === "form"
        ? "form"
        : pollTypeFromUrl === "quizz"
          ? "quizz"
          : "date";

  // Convertir "resume" en "conversationId" si nécessaire (Session 2 - Bug 3)
  useEffect(() => {
    if (resumeId && !conversationId) {
      const newUrl = `${location.pathname}?conversationId=${resumeId}`;
      navigate(newUrl, { replace: true });
    }
  }, [resumeId, conversationId, location.pathname, navigate]);

  const chatKey =
    resumeId || conversationId || editPollId || newChatTimestamp || "new-chat";

  // Get currentPoll FIRST before using it in other hooks
  const { isEditorOpen, currentPoll } = useEditorState();

  // Hook de données refactorisé (Story 2) - NOW currentPoll is available
  const { recentPolls, conversations } = useWorkspaceData({
    chatKey,
    currentPoll,
    conversationsRefreshKey: 0,
  });

  // Get media queries AFTER currentPoll is available
  const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1023px)");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { openEditor, closeEditor, setCurrentPoll, createPollFromChat } =
    useEditorActions();

  // Si on est en mode édition et sur mobile, commencer par l'onglet editor
  const initialTab = editPollId && isMobile ? "editor" : "chat";

  console.log("AICreationWorkspace - Mode détection:", {
    editPollId,
    isMobile,
    initialTab,
    location: location.pathname + location.search,
  });

  // 🔥 NOUVEAU: Si on a editPollId mais pas conversationId, trouver la conversation associée
  useEffect(() => {
    if (editPollId && !conversationId && conversations.length > 0) {
      // Chercher la conversation associée à ce poll
      const associatedConversation = conversations.find(
        (conv) => conv.pollId === editPollId,
      );

      if (associatedConversation) {
        // Rediriger vers la même URL mais avec conversationId
        const newUrl = `${location.pathname}?edit=${editPollId}&conversationId=${associatedConversation.id}`;
        navigate(newUrl, { replace: true });
      }
    }
  }, [editPollId, conversationId, conversations, location.pathname, navigate]);

  // Hook de dépendances refactorisé (Story 2)
  const dependencies = useWorkspaceDependencies();
  const { user, voiceRecognition, modals } = dependencies;

  // 🔒 Guard création/édition : si ?edit=<id>, vérifier que le visiteur
  // courant est bien le créateur du sondage avant d'afficher l'éditeur.
  // Sans ça, n'importe qui peut forger l'URL pour ouvrir l'éditeur d'un
  // sondage tiers (la RLS côté serveur bloque l'UPDATE, mais l'UI doit
  // aussi refuser d'afficher le formulaire).
  //
  // Deux chemins d'identification (lib/canEditPoll) :
  // - Créateur authentifié → poll.creator_id === user.id
  // - Créateur anonyme     → ce navigateur détient le jeton de propriétaire
  //                          du sondage (vérifié par la base à l'enregistrement)
  const {
    data: editTargetPoll,
    isLoading: editTargetLoading,
    error: editTargetError,
  } = usePoll(editPollId || "", { enabled: !!editPollId });

  useEffect(() => {
    if (!editPollId) return;
    if (editTargetLoading) return;

    // Si le poll n'a pas pu être chargé (supprimé, introuvable, RLS SELECT refuse)
    if (editTargetError || !editTargetPoll) {
      toast({
        title: t("errors:poll_not_found_title"),
        description: t("errors:poll_not_found_description"),
        variant: "destructive",
      });
      navigate("/dashboard", { replace: true });
      return;
    }

    // Compte propriétaire, ou navigateur détenant le jeton de propriétaire de
    // ce sondage invité (cf. lib/canEditPoll). L'empreinte d'appareil n'est
    // plus comparée : elle n'est plus une preuve de possession côté serveur.
    const isCreator = canOpenPollEditor({
      poll: editTargetPoll as {
        creator_id?: string | null;
        guest_fingerprint?: string | null;
      },
      userId: user?.id ?? null,
      hasOwnerToken: !!getOwnerTokenForPoll(editPollId),
    });

    if (!isCreator) {
      toast({
        title: t("errors:access_denied_title"),
        description: t("errors:poll_edit_creator_only"),
        variant: "destructive",
      });
      navigate(`/poll/${editTargetPoll.slug || editPollId}/results`, {
        replace: true,
      });
      return;
    }

    // Hydrate editor with the poll data
    setCurrentPoll(editTargetPoll as Poll);
    if (!isMobile) {
      openEditor();
    }

    // Télémétrie usage réel (admin) : l'utilisateur revient éditer un
    // sondage existant (chat ou éditeur manuel selon initialTab). Best-effort.
    void UsageAnalyticsTracker.track("poll_reopened_for_edit", {
      pollId: editPollId,
      pollType: (editTargetPoll as Poll).type ?? null,
    });
  }, [
    editPollId,
    editTargetPoll,
    editTargetLoading,
    editTargetError,
    user?.id,
    navigate,
    toast,
    t,
    setCurrentPoll,
    openEditor,
    isMobile,
  ]);

  // Mapping des états de modale pour minimiser les changements JSX
  const authModalOpen = modals.auth.open;
  const setAuthModalOpen = modals.auth.setOpen;
  const signOutDialogOpen = modals.signOut.open;
  const setSignOutDialogOpen = modals.signOut.setOpen;
  const betaKeyModalOpen = modals.betaKey.open;
  const setBetaKeyModalOpen = modals.betaKey.setOpen;

  const {
    mobileActiveTab,
    setMobileActiveTab,
    showManualEditorOnMobile,
    setShowManualEditorOnMobile,
    showBothSides,
  } = useWorkspaceNavigation({
    initialTab: initialTab,
    pollType: pollTypeForComponents,
    isTablet,
  });

  // Desktop : l'utilisateur peut réduire ou masquer l'assistant IA pour
  // donner toute la largeur à l'éditeur. Choix mémorisé (préférence UI).
  const [aiPanelMode, setAiPanelModeState] = useState<
    "split" | "collapsed" | "hidden"
  >(() => {
    try {
      const saved = localStorage.getItem(AI_PANEL_MODE_KEY);
      return saved === "collapsed" || saved === "hidden" ? saved : "split";
    } catch {
      return "split";
    }
  });
  const setAiPanelMode = useCallback(
    (mode: "split" | "collapsed" | "hidden") => {
      setAiPanelModeState(mode);
      try {
        localStorage.setItem(AI_PANEL_MODE_KEY, mode);
      } catch {
        // préférence non persistée (navigation privée) : sans conséquence
      }
    },
    [],
  );
  const canTogglePanel = !isMobile && !isTablet && showBothSides;

  // Forcer l'onglet éditeur si on est en mode édition sur mobile
  useEffect(() => {
    if (editPollId && isMobile && mobileActiveTab !== "editor") {
      console.log("AICreationWorkspace - Forcing editor tab for edit mode");
      setMobileActiveTab("editor");
      setShowManualEditorOnMobile(true);
    }
  }, [
    editPollId,
    isMobile,
    mobileActiveTab,
    setMobileActiveTab,
    setShowManualEditorOnMobile,
  ]);

  const [publishedPoll, setPublishedPoll] = useState<Poll | null>(null);

  // Télémétrie usage réel (admin) : un sondage vient d'être sauvegardé
  // depuis l'éditeur manuel (onglet "editor" de WorkspaceEditor), qu'il
  // s'agisse d'une création from scratch ou de la finalisation manuelle
  // d'une proposition IA (hadAiOrigin). Best-effort, jamais bloquant.
  const handlePollPublished = useCallback(
    (poll: Poll) => {
      void UsageAnalyticsTracker.track("manual_edit", {
        pollId: poll.id ?? null,
        pollType: poll.type ?? pollTypeFromUrl,
        hadAiOrigin:
          (currentPoll as { aiPrefilled?: boolean } | null)?.aiPrefilled ===
          true,
        isNewPoll: !currentPoll,
      });

      // Relie le sondage à la conversation IA : sa réouverture en édition
      // réaffichera l'historique du chat. Best-effort, jamais bloquant.
      const chatConversationId =
        chatRef.current?.getConversationId?.() ?? conversationId;
      if (chatConversationId && poll.id) {
        void linkPublishedPollToConversation(
          chatConversationId,
          { id: poll.id, type: poll.type, status: poll.status },
          user?.id ?? null,
        ).catch((error) => {
          logger.warn("Lien conversation ↔ sondage non écrit", "poll", {
            error,
          });
        });
      }

      setPublishedPoll(poll);
    },
    [currentPoll, pollTypeFromUrl, conversationId, user?.id],
  );

  // 🔥 FIX: Reset the success screen when a new conversation/reset is triggered
  useEffect(() => {
    const handleChatReset = () => {
      setPublishedPoll(null);
    };

    window.addEventListener("chat-reset", handleChatReset);
    return () => {
      window.removeEventListener("chat-reset", handleChatReset);
    };
  }, []);

  // 📜 DOCTRINE PRICING : la création de sondages est illimitée pour tous les plans.
  //    Il n'existe donc aucun "pollQuotaExceeded" à écouter. Les plafonds IA
  //    (conversations par plan) sont tenus côté serveur + QuotaLimitModal.

  const { isMobile: isMobileFromContext, setIsSidebarOpen } = useUIState();
  const isMobileFromMediaQuery = useMediaQuery("(max-width: 768px)");
  // Utiliser la détection directe si le contexte n'est pas disponible
  const isMobileEffective = isMobileFromContext || isMobileFromMediaQuery;

  // Fermer le preview et nettoyer le poll quand on démarre un nouveau chat (Session 2 - Bug 4)
  useEffect(() => {
    if (newChatTimestamp && !conversationId && !resumeId && !editPollId) {
      closeEditor();
      setCurrentPoll(null);
    }
  }, [
    newChatTimestamp,
    conversationId,
    resumeId,
    editPollId,
    closeEditor,
    setCurrentPoll,
  ]);

  // 🔥 NOUVEAU: Créer un sondage par défaut quand on arrive depuis la landing page sans conversation
  useEffect(() => {
    const isFromLanding =
      location.pathname.includes("/workspace/") &&
      (location.pathname.includes("/date") ||
        location.pathname.includes("/form") ||
        location.pathname.includes("/availability"));

    if (
      isFromLanding &&
      !conversationId &&
      !resumeId &&
      !editPollId &&
      !currentPoll &&
      !newChatTimestamp
    ) {
      const creatorId = user?.id || getCurrentUserId();
      const defaultPoll =
        pollTypeForComponents === "form"
          ? {
              id: `default-form-${Date.now()}`,
              creator_id: creatorId,
              title: t("creator:default_form_title"),
              slug: `${t("creator:default_form_slug")}-${Date.now()}`,
              status: "draft" as const,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              type: "form" as const,
              questions: [
                {
                  id: "q1",
                  kind: "text" as const,
                  title: t("creator:default_question_title"),
                  type: "text" as const,
                  required: true,
                },
              ],
            }
          : pollTypeForComponents === "availability"
            ? {
                id: `default-availability-${Date.now()}`,
                creator_id: creatorId,
                title: t("creator:default_availability_title"),
                slug: `${t("creator:default_availability_slug")}-${Date.now()}`,
                status: "draft" as const,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                type: "availability" as const,
              }
            : {
                id: `default-date-${Date.now()}`,
                creator_id: creatorId,
                title: t("creator:default_date_title"),
                slug: `${t("creator:default_date_slug")}-${Date.now()}`,
                status: "draft" as const,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                type: "date" as const,
                settings: {
                  showTimeSlots: true,
                  selectedDates: [
                    new Date().toISOString().split("T")[0],
                    new Date(Date.now() + 86400000).toISOString().split("T")[0],
                  ],
                  showLogo: true,
                },
              };

      setCurrentPoll(defaultPoll);

      if (!isMobile) {
        setTimeout(() => {
          openEditor();
        }, 100);
      }
    }
  }, [
    location.pathname,
    conversationId,
    resumeId,
    editPollId,
    currentPoll,
    newChatTimestamp,
    pollTypeForComponents,
    setCurrentPoll,
    isMobile,
    openEditor,
    user,
    t,
  ]);

  const conversationContext = useConversation();
  const clearConversation = useMemo(
    () => conversationContext?.clearConversation || (() => {}),
    [conversationContext?.clearConversation],
  );

  // Au chargement, le chat réaffiche la conversation en cours alors que
  // l'éditeur repart à vide (il ne se restaure qu'avec ?conversationId).
  // Une seule fois, l'éditeur reprend ce que montre la dernière carte du chat
  // — jamais en édition d'un sondage existant (?edit=).
  const chatProposal = conversationContext?.currentPoll ?? null;
  const chatHasProposalCard = (conversationContext?.messages ?? []).some(
    (m) => !!m.metadata?.pollSuggestion,
  );
  const reloadSyncDoneRef = useRef(false);
  useEffect(() => {
    if (reloadSyncDoneRef.current || editPollId) return;
    if (pollTypeForComponents !== "date") return;
    if (!chatProposal || chatProposal.type !== "date" || !chatHasProposalCard)
      return;
    reloadSyncDoneRef.current = true;
    setCurrentPoll(chatProposal);
    openEditor();
  }, [
    chatProposal,
    chatHasProposalCard,
    editPollId,
    pollTypeForComponents,
    setCurrentPoll,
    openEditor,
  ]);

  useEffect(() => {
    if (newChatTimestamp) {
      clearConversation();
      const currentPath = location.pathname;
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("new");
      const newUrl = searchParams.toString()
        ? `${currentPath}?${searchParams.toString()}`
        : currentPath;
      navigate(newUrl, { replace: true });

      setTimeout(() => {
        openEditor();
      }, 100);
    }
  }, [
    newChatTimestamp,
    clearConversation,
    openEditor,
    navigate,
    location.pathname,
    location.search,
  ]);

  if (publishedPoll) {
    const paddingTop = externalHeaderHeight
      ? `${externalHeaderHeight / 4}rem`
      : "pt-20";

    // Audience info attachée par PollCreator pour personnaliser le succès.
    const audience = (publishedPoll as any).__audience as
      | {
          mode: "group" | "emails" | "link";
          sentCount: number;
          groupName?: string;
          emailDelivered: boolean;
          invitedEmails?: string[];
        }
      | undefined;

    const baseDescriptionByType =
      publishedPoll.type === "date"
        ? t("creator:success_base_description_date", {
            title: publishedPoll.title,
          })
        : publishedPoll.type === "form"
          ? t("creator:success_base_description_form", {
              title: publishedPoll.title,
            })
          : t("creator:success_base_description_availability", {
              title: publishedPoll.title,
            });

    let audienceTitle: string | undefined;
    let audienceDescription: string | undefined;
    if (audience) {
      if (audience.mode === "group" && audience.emailDelivered) {
        audienceTitle = t("creator:success_audience_group_title", {
          groupName: audience.groupName ?? t("creator:group_unnamed"),
        });
        audienceDescription = t("creator:success_audience_group_description", {
          count: audience.sentCount,
          groupName: audience.groupName ?? t("creator:group_unnamed"),
        });
      } else if (audience.mode === "emails" && audience.emailDelivered) {
        audienceTitle = t("creator:success_audience_emails_title");
        audienceDescription = t("creator:success_audience_emails_description", {
          count: audience.sentCount,
        });
      } else if (audience.mode === "link") {
        audienceTitle = t("creator:success_audience_link_title");
        audienceDescription = t("creator:success_audience_link_description");
      } else if (!audience.emailDelivered) {
        audienceDescription = t(
          "creator:success_audience_email_failed_description",
        );
      }
    }

    // Écran de succès de publication — suit toujours --primary (le thème
    // actif), jamais le type de sondage (voir memoire ui-themes).
    const themeColors = {
      bgGradient: "from-primary/20 to-primary/10",
      borderColor: "border-primary/30",
      iconBg: "bg-primary/20",
      iconColor: "text-primary",
      title:
        audienceTitle ??
        (publishedPoll.type === "date"
          ? t("creator:success_title_date")
          : publishedPoll.type === "form"
            ? t("creator:success_title_form")
            : t("creator:success_title_availability")),
      description: audienceDescription ?? baseDescriptionByType,
    };

    return (
      <div className="min-h-screen-safe bg-background w-full flex items-center justify-center">
        <div className={`${paddingTop} w-full`}>
          <div className="max-w-2xl mx-auto p-4 sm:p-6">
            <div
              className={`bg-gradient-to-br ${themeColors.bgGradient} rounded-lg ${themeColors.borderColor} p-8 text-center space-y-6`}
            >
              <div className="flex justify-center">
                <div
                  className={`w-16 h-16 ${themeColors.iconBg} rounded-full flex items-center justify-center`}
                >
                  <Check className={`w-10 h-10 ${themeColors.iconColor}`} />
                </div>
              </div>

              <div>
                <h1
                  className="text-3xl font-bold text-foreground mb-2"
                  data-testid="success-message"
                >
                  {themeColors.title}
                </h1>
                <p className="text-muted-foreground">
                  {themeColors.description}
                </p>
                {audience?.mode === "emails" &&
                  audience.emailDelivered &&
                  !!audience.invitedEmails?.length && (
                    <div className="mt-3 flex justify-center">
                      <SaveEmailsAsGroupCta emails={audience.invitedEmails} />
                    </div>
                  )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-semibold transition-all shadow-lg"
                    data-testid="go-to-dashboard-button"
                  >
                    <Check className="w-5 h-5" />
                    {t("creator:go_to_dashboard")}
                  </Link>
                ) : (
                  <button
                    disabled
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary/50 text-primary-foreground rounded-lg font-semibold opacity-50 cursor-not-allowed"
                    title={t("auth_required_title")}
                    data-testid="go-to-dashboard-button-locked"
                  >
                    <Lock className="w-5 h-5" />
                    {t("creator:go_to_dashboard")}
                  </button>
                )}
                <Link
                  to={`/poll/${publishedPoll.slug || publishedPoll.id}/vote`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent text-muted-foreground border-2 border-border rounded-lg font-semibold hover:bg-accent transition-colors"
                  data-testid="view-poll-button"
                >
                  <ExternalLink className="w-5 h-5" />
                  {publishedPoll.type === "form"
                    ? t("creator:view_form")
                    : t("creator:view_poll")}
                </Link>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-3">
                  {t("creator:share_link_label")}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
                  <code
                    className="px-4 py-2 bg-muted border border-border rounded text-sm font-mono text-muted-foreground break-all"
                    data-testid="share-link"
                  >
                    {window.location.origin}/poll/
                    {publishedPoll.slug || publishedPoll.id}/vote
                  </code>
                  <button
                    onClick={() => {
                      if (isMobile) setIsSidebarOpen(false);
                      const url = `${window.location.origin}/poll/${publishedPoll.slug || publishedPoll.id}/vote`;
                      navigator.clipboard.writeText(url);
                      toast({
                        title: t("creator:link_copied_title"),
                        description: t("creator:link_copied_description"),
                      });
                    }}
                    className="px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm font-medium whitespace-nowrap"
                    data-testid="copy-link-button"
                  >
                    {t("common:copy")}
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-border flex justify-center">
                <QRCodeDisplay
                  url={`${window.location.origin}/poll/${publishedPoll.slug || publishedPoll.id}/vote`}
                  size={200}
                  label={t("creator:qr_scan_label")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ResponsiveLayout
        header={
          <ProductHeader
            productType={pollTypeForComponents}
            showAuth={true}
            showAuthButtons={!user}
          />
        }
        activeTab={mobileActiveTab === "chat" ? "left" : "right"}
        onTabChange={(tab) => {
          const newTab = tab === "left" ? "chat" : "editor";
          console.log(
            "AICreationWorkspace - Changement onglet (ResponsiveLayout):",
            {
              currentTab: mobileActiveTab,
              clickedTab: tab,
              newTab,
              isMobile,
              editPollId,
            },
          );
          setMobileActiveTab(newTab);
          if (newTab === "chat") {
            setShowManualEditorOnMobile(false);
          } else {
            setShowManualEditorOnMobile(true);
          }
        }}
        showBothSides={showBothSides}
        leftPanelMode={canTogglePanel ? aiPanelMode : "split"}
        rightOverlay={
          aiPanelMode === "hidden" ? (
            <button
              type="button"
              onClick={() => setAiPanelMode("split")}
              className="absolute bottom-24 left-6 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border shadow-md text-sm font-medium text-foreground hover:bg-accent"
              data-testid="ai-panel-reopen"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              {t("creator:ai_panel.reopen")}
            </button>
          ) : undefined
        }
        leftContent={
          <div className="flex flex-col h-full">
            {canTogglePanel && (
              <div className="flex justify-end gap-1 px-2 pt-2 shrink-0">
                <button
                  type="button"
                  onClick={() =>
                    setAiPanelMode(
                      aiPanelMode === "collapsed" ? "split" : "collapsed",
                    )
                  }
                  aria-label={t(
                    aiPanelMode === "collapsed"
                      ? "creator:ai_panel.expand"
                      : "creator:ai_panel.collapse",
                  )}
                  title={t(
                    aiPanelMode === "collapsed"
                      ? "creator:ai_panel.expand"
                      : "creator:ai_panel.collapse",
                  )}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
                  data-testid="ai-panel-collapse"
                >
                  {aiPanelMode === "collapsed" ? (
                    <PanelLeftOpen className="w-4 h-4" />
                  ) : (
                    <PanelLeftClose className="w-4 h-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setAiPanelMode("hidden")}
                  aria-label={t("creator:ai_panel.hide")}
                  title={t("creator:ai_panel.hide")}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
                  data-testid="ai-panel-hide"
                >
                  <EyeOff className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="flex-1 min-h-0">
              <ChatContainer>
                <GeminiChatInterface
                  ref={chatRef}
                  key={chatKey}
                  pollType={pollTypeForComponents}
                  onPollCreated={async (pollData) => {
                    await createPollFromChat(pollData as any);
                    if (isMobile) {
                      setMobileActiveTab("editor");
                      setShowManualEditorOnMobile(false);
                    }
                  }}
                  onProposalChange={(proposal) => {
                    // Aucune écriture en base ici : l'éditeur prend la
                    // proposition, la validation reste « Finaliser la création ».
                    setCurrentPoll(proposal);
                    openEditor();
                  }}
                  onUserMessage={() => {}}
                  hideStatusBar={true}
                  darkTheme={true}
                  voiceRecognition={voiceRecognition}
                />
              </ChatContainer>
            </div>
          </div>
        }
        rightContent={
          <WorkspaceEditor
            isMobile={isMobile}
            showManualEditorOnMobile={showManualEditorOnMobile}
            setShowManualEditorOnMobile={setShowManualEditorOnMobile}
            isEditorOpen={isEditorOpen}
            closeEditor={closeEditor}
            currentPoll={currentPoll}
            pollTypeFromUrl={pollTypeFromUrl}
            setMobileActiveTab={setMobileActiveTab}
            setPublishedPoll={handlePollPublished}
          />
        }
        mobileNavButtons={
          pollTypeForComponents !== "availability" &&
          pollTypeForComponents !== "quizz" ? (
            <MobileNavTabs
              activeTab={mobileActiveTab}
              onTabChange={(tab: any) => {
                const newTab = tab; // MobileNavTabs utilise directement 'chat' ou 'editor'
                console.log(
                  "AICreationWorkspace - Changement onglet (MobileNavTabs):",
                  {
                    currentTab: mobileActiveTab,
                    clickedTab: tab,
                    newTab,
                    isMobile,
                    editPollId,
                  },
                );
                setMobileActiveTab(newTab);
                if (newTab === "chat") {
                  setShowManualEditorOnMobile(false);
                } else if (newTab === "editor") {
                  setShowManualEditorOnMobile(true);
                }
              }}
              pollType={pollTypeForComponents}
              hasPoll={!!publishedPoll}
            />
          ) : undefined
        }
      />

      <WorkspaceModals dependencies={dependencies} />
    </>
  );
}
